<script setup lang="ts">
/**
 * The icon for an OS / marketplace template.
 *
 * Prefers what staff chose in the Super Admin Console (`icon_url`) and falls
 * back to the name-matching heuristic the caller supplies. Before this, the
 * heuristic was the ONLY mechanism and lived duplicated in the checkout picker
 * and the reinstall modal — so a new template had no recognisable icon until
 * someone shipped a frontend release, and `icon_url` (bare legacy filenames
 * like `zabbix.png`) rendered nowhere at all.
 *
 * `icon_url` may hold either an Iconify name from a bundled collection or an
 * https URL, for the brands no icon set carries (3CX, Issabel, MK-Auth…).
 * `resolveTemplateIcon` (`~/utils/template-catalog`) decides which — the same
 * call `TemplateLogo` makes to choose the colour it paints this icon in.
 */
const props = defineProps<{
  /** Value stored on the template. Null/empty/legacy → the fallback is used. */
  iconUrl?: string | null
  /** Iconify name derived from the template name — always a valid icon. */
  fallback: string
  /** Sizing classes, e.g. `size-5`. */
  class?: string
}>()

const resolved = computed(() => resolveTemplateIcon(props.iconUrl, props.fallback))

/** A remote image that 404s must not leave a broken-image glyph in the card. */
const imageFailed = ref(false)
watch(() => props.iconUrl, () => { imageFailed.value = false })
</script>

<template>
  <UIcon
    v-if="resolved.kind === 'icon' || imageFailed"
    :name="resolved.kind === 'icon' ? resolved.value : fallback"
    :class="props.class"
  />
  <img
    v-else
    :src="resolved.value"
    alt=""
    loading="lazy"
    :class="[props.class, 'object-contain']"
    @error="imageFailed = true"
  >
</template>
